import { ClerkOfflineError } from '@clerk/shared/error'
import * as Sentry from '@sentry/react'

import type { GetToken } from '@clerk/shared/types'

// Global token getter function
let globalGetToken: GetToken | null = null

/**
 * Set the global getToken function from Clerk
 * @param getToken - Function to retrieve the auth token from Clerk
 */
export function setGlobalGetToken(getToken: GetToken) {
	globalGetToken = getToken
}

/**
 * Get the current auth token
 * @param options - Options to control token retrieval behavior
 * @throws Error if token provider is not initialized (pre-init request = bug)
 */
export async function getAuthToken(options?: {
	skipCache?: boolean
}): Promise<string | null> {
	const { skipCache = false } = options || {}

	if (!globalGetToken) {
		throw new Error('Token provider not initialized')
	}

	try {
		return await globalGetToken({ skipCache })
	} catch (error) {
		if (ClerkOfflineError.is(error)) {
			return null
		}
		throw error
	}
}

/**
 * Decode the `exp` claim (seconds since epoch) from a JWT without verifying.
 * Returns null if the token is malformed or has no `exp` claim.
 */
function readJwtExp(token: string): number | null {
	const parts = token.split('.')
	if (parts.length !== 3) return null
	try {
		const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
		const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
		const json = atob(padded)
		const decoded = JSON.parse(json) as { exp?: unknown }
		return typeof decoded.exp === 'number' ? decoded.exp : null
	} catch {
		return null
	}
}

/**
 * Get an auth token, force-refreshing when its remaining lifetime is below
 * `minSecondsRemaining`. Used as a preflight before streaming chat requests so
 * a short stream doesn't start with a near-expired JWT. Mid-stream refresh is
 * not handled here.
 *
 * @param minSecondsRemaining - leeway window in seconds (default 120)
 */
export async function getTokenWithLeeway(
	minSecondsRemaining = 120,
): Promise<string | null> {
	const token = await getAuthToken()
	if (!token) return null

	const exp = readJwtExp(token)
	if (exp === null) {
		Sentry.addBreadcrumb({
			category: 'auth',
			level: 'warning',
			message:
				'getTokenWithLeeway: failed to parse JWT exp, returning cached token',
		})
		return token
	}

	const nowSeconds = Math.floor(Date.now() / 1000)
	if (exp - nowSeconds < minSecondsRemaining) {
		return getAuthToken({ skipCache: true })
	}
	return token
}

/**
 * Check if the token provider is initialized
 */
export function isTokenProviderReady(): boolean {
	return globalGetToken !== null
}

/**
 * Reset the token provider. Tests only — nothing in `src/` re-initialises it,
 * and a second `setTokenProvider` overwrites rather than needing a reset first.
 *
 * @internal — so `check:dead`, which runs knip in production mode, does not
 * read the test importer as a consumer.
 */
export function resetTokenProvider() {
	globalGetToken = null
}
